import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { cartCheck, createOrder, pollUntilPaid } from "./api";

const SYM = { USD: "$", INR: "₹", EUR: "€", GBP: "£", AUD: "A$", CAD: "C$" };

/** Format a price + currency (e.g. 199 / USD → "$199.00"). */
export function money(price, currency) {
  const n = Number(price || 0);
  const s = SYM[currency];
  return s ? `${s}${n.toFixed(2)}` : `${n.toFixed(2)} ${currency || ""}`.trim();
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const round2 = (n) => Math.round(Number(n || 0) * 100) / 100;

/**
 * Resolve the checkout copy from the API `data`, falling back to sensible
 * defaults — every CheckoutTemplate{N} uses the same resolved values so the
 * designer's Header / Order overrides work identically across all 17.
 */
export function copy(data = {}) {
  return {
    brand: data.community_name || "Your Course",
    h1: (data.headline || "").trim() || "Complete your enrollment",
    sub: (data.sub_headline || "").trim() || "Instant, lifetime access — you're one step away.",
    label: (data.summary_label || "").trim() || data.course_name || "Course enrollment",
    guarantee: (data.guarantee || "").trim() || "Secure checkout · 30-day money-back guarantee",
    guaranteeOn: data.guarantee_status !== false,
    total: money(data.price, data.currency),
    logo: data.logo || null,
  };
}

const PLACEHOLDERS = {
  first_name: "Jane Doe",
  email: "jane@example.com",
  phone: "+1 555 123 4567",
  billing_address: "123 Main St",
  city: "City",
  state: "State",
  country: "Country",
};

/** The plan's net amount (price + one-time setup fee). */
function planAmount(plan) {
  if (!plan) return 0;
  return Number(plan.price || 0) + Number(plan.setup_fee || 0);
}

/**
 * The whole checkout: buyer fields + plan selection + coupon + gateway + order
 * submission. Centralised here so all 17 CheckoutTemplate{N} share identical
 * behaviour — they only differ in chrome (nav / hero / footer / CSS prefix).
 *
 * `code` is the course code (from the route); `data` is the fetched payload.
 */
export function useCheckout(code, data = {}) {
  const fields = useMemo(() => (Array.isArray(data.fields) ? data.fields : []), [data.fields]);
  const plans = useMemo(() => (Array.isArray(data.plans) ? data.plans : []), [data.plans]);
  const gateways = useMemo(() => (Array.isArray(data.gateways) ? data.gateways : []), [data.gateways]);
  const bumps = useMemo(() => (Array.isArray(data.bumps) ? data.bumps : []), [data.bumps]);
  const purchasable = !!data.purchasable;
  const trust = data.trust || {};

  // Order bumps: pre-checked ones (bump_option === "1") start selected.
  const [selectedBumps, setSelectedBumps] = useState(() =>
    bumps.filter((b) => String(b.bump_option) === "1").map((b) => b.bump_product_id)
  );
  const toggleBump = (id) =>
    setSelectedBumps((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const bumpTotal = useMemo(
    () => bumps.filter((b) => selectedBumps.includes(b.bump_product_id)).reduce((n, b) => n + Number(b.bump_price || 0), 0),
    [bumps, selectedBumps]
  );

  // Terms & Conditions acceptance (pre-accepted when not required).
  const [accepted, setAccepted] = useState(!(trust.terms_show && trust.terms_required));

  // Default to the preferred plan (plans come preferred-first) and, when a
  // payment is actually due, the first gateway.
  const [form, setForm] = useState(() => {
    const base = { coupon: "" };
    fields.forEach((f) => (base[f.name] = ""));
    base.pricing_id = plans[0]?.pricing_id ?? null;
    base.integration_id = gateways[0]?.integration_id ?? 0;
    return base;
  });

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));
  const selectPlan = (id) => setForm((s) => ({ ...s, pricing_id: Number(id) }));
  const selectGateway = (id) => setForm((s) => ({ ...s, integration_id: Number(id) }));

  const selectedPlan = useMemo(
    () => plans.find((p) => p.pricing_id === Number(form.pricing_id)) || plans[0] || null,
    [plans, form.pricing_id]
  );
  const currency = selectedPlan?.currency || data.currency || "USD";
  const baseAmount = selectedPlan ? planAmount(selectedPlan) : Number(data.price || 0);

  // Coupon state, recomputed server-side (never trust client math).
  const [coupon, setCoupon] = useState({ status: null, discount: 0, after: baseAmount, message: "" });
  const [checking, setChecking] = useState(false);

  // Re-price whenever the plan or bump selection changes: clear the stale
  // coupon result (server recomputes on re-apply, with bumps folded in).
  useEffect(() => {
    setCoupon({ status: null, discount: 0, after: baseAmount + bumpTotal, message: "" });
  }, [form.pricing_id, selectedBumps]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyCoupon = useCallback(async () => {
    if (!selectedPlan) return;
    setChecking(true);
    try {
      const r = await cartCheck(code, {
        pricing_id: selectedPlan.pricing_id,
        coupon_code: form.coupon || "",
        bump: selectedBumps,
      });
      setCoupon({
        status: r.coupon_status,
        discount: Number(r.coupon_discount || 0),
        after: Number(r.after_discount ?? baseAmount + bumpTotal),
        message: r.coupon_message || (r.coupon_status ? "Coupon applied" : "Invalid coupon code"),
      });
    } catch {
      setCoupon({ status: false, discount: 0, after: baseAmount + bumpTotal, message: "Could not check the coupon" });
    } finally {
      setChecking(false);
    }
  }, [code, selectedPlan, form.coupon, baseAmount, bumpTotal, selectedBumps]);

  const payable = coupon.status ? coupon.after : round2(baseAmount + bumpTotal);
  const isFree = payable <= 0;

  // Submission.
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const doneRef = useRef(false);

  const validate = () => {
    const errs = {};
    fields.forEach((f) => {
      const v = (form[f.name] || "").trim();
      if (f.required && !v) errs[f.name] = `${f.label} is required`;
      if (f.name === "email" && v && !EMAIL_RE.test(v)) errs.email = "Please enter a valid email address";
    });
    if (!form.pricing_id) errs.pricing_id = "Please select a plan";
    if (!isFree && !form.integration_id) errs.integration_id = "Please choose a payment method";
    if (trust.terms_show && trust.terms_required && !accepted) errs.terms = "Please accept the terms to continue";
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    setError("");
    if (submitting || doneRef.current) return;
    if (!purchasable && !isFree) {
      setError("This course isn't available for purchase yet.");
      return;
    }
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = { ...form, pricing_id: Number(form.pricing_id), integration_id: Number(form.integration_id) };
      delete payload.coupon;
      payload.coupon_code = form.coupon || "";
      payload.bump = selectedBumps;
      const r = await createOrder(code, payload);
      if (r?.status) {
        doneRef.current = true;
        // Settle contract (mirrors ft-checkout.js):
        //  flow_type 0/free or the local stub → { redirect } straight to thanks.
        //  flow_type 1 (redirect gateway)      → go to payment_url, gateway
        //                                         returns and the thanks page polls.
        //  flow_type 2 (SDK drop-in)           → poll order-status here, then
        //                                         handle-payment-response → redirect.
        if (Number(r.flow_type) === 2 && r.payment_id) {
          const dest = await pollUntilPaid(code, r.payment_id);
          window.location.href = dest || r.redirect || r.payment_url;
        } else if (r.redirect) {
          window.location.href = r.redirect;
        } else if (r.payment_url) {
          window.location.href = r.payment_url;
        } else {
          setError(""); // order recorded; no thank-you page wired
          setCoupon((c) => ({ ...c, message: r.message || "Order placed successfully." }));
        }
      } else {
        setError(r?.message || "We couldn't place your order. Please try again.");
      }
    } catch (err) {
      const res = err?.response?.data;
      if (res?.errors) {
        const flat = {};
        Object.entries(res.errors).forEach(([k, v]) => (flat[k] = Array.isArray(v) ? v[0] : String(v)));
        setFieldErrors(flat);
      }
      setError(res?.message || "Something went wrong placing your order.");
    } finally {
      setSubmitting(false);
    }
  };

  return {
    fields,
    plans,
    gateways,
    purchasable,
    form,
    set,
    selectPlan,
    selectGateway,
    selectedPlan,
    currency,
    baseAmount,
    bumps,
    selectedBumps,
    toggleBump,
    bumpTotal,
    coupon,
    checking,
    applyCoupon,
    payable,
    isFree,
    submit,
    submitting,
    error,
    fieldErrors,
    trust,
    accepted,
    setAccepted,
    placeholder: (name) => PLACEHOLDERS[name] || "",
    fmt: (n) => money(n, currency),
  };
}
